import React from 'react';
import { Avatar, Chip, Link, Snippet } from '@heroui/react';
import { Icon } from '@iconify/react';
import { TransactionData } from '../types';
import { LinkIcon } from '../../../../components/icons';
import { IdView, Typography } from '../../../../components';
import { ResponsiveTooltip } from '../../../../components/ui';
import { useTranslations } from '../../../../contexts/translation.context';

interface TransactionCampaignCellProps {
  transaction: TransactionData;
}

const TransactionCampaignCell: React.FC<TransactionCampaignCellProps> = ({ transaction }) => {
  const { t } = useTranslations();

  const getCampaignNameFromLink = (link?: string) => {
    if (!link) return t('transactions.campaign.unknown');

    try {
      const url = new URL(link);
      const cleanedPath = url.pathname.replace(/\/+$/, '');
      const segments = cleanedPath.split('/').filter(Boolean);
      if (segments.length) return segments[segments.length - 1];
      return url.hostname;
    } catch {
      const cleanedLink = link.replace(/\/+$/, '');
      const parts = cleanedLink.split('/').filter(Boolean);
      return parts.length ? parts[parts.length - 1] : link;
    }
  };

  const formatCampaignDate = (date?: string) => {
    if (!date) return '';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleDateString('ru-RU');
  };

  const companyLink = transaction.company?.link || '';
  const campaignName = getCampaignNameFromLink(companyLink)?.slice(-4);
  const campaignDate = formatCampaignDate(transaction.company?.createdAt);
  const chipLabel = [campaignName, campaignDate].filter(Boolean).join(' ');
  const linkHref = companyLink || '#';
  const linkLabel = companyLink || t('transactions.campaign.unknown');
  const clicksCount = transaction.company?.clicks ?? 0;
  const subscribedCount = transaction.company?.subscribedCount ?? 0;
  const subscribersPercent =
    clicksCount > 0 ? ((subscribedCount / clicksCount) * 100).toFixed(2) : '0.00';

  return (
    <div className="flex flex-col gap-1">
      {/* Имя пользователя с иконкой */}
      <div className="flex items-center gap-1">
        <Avatar
          src={transaction.partner.avatar}
          showFallback
          name={transaction?.partner.name?.slice(0, 1)}
          className="w-5 h-5 rounded-full"
        />
        <span className="font-medium">{transaction.partner.name}</span>
      </div>

      {/* Аватар модели и ссылка на OnlyFans */}
      <div className="flex items-center gap-2">
        <Avatar
          src={transaction.model.avatar}
          showFallback
          name={transaction.model.ofUserName?.slice(0, 1)}
          className="w-5 h-5 rounded-1"
        />
        <ResponsiveTooltip content={t('transactions.campaign.modelIdTooltip')} placement="top">
          <div>
            <IdView textToCopy={transaction.model?.ofId}>
              <span>#{transaction.model?.ofId?.toString()?.slice(-4)}</span>
            </IdView>
          </div>
        </ResponsiveTooltip>
        <ResponsiveTooltip content={t('transactions.campaign.nicknameTooltip')} placement="top">
          <Link
            href={`https://onlyfans.com/${transaction.model.ofUserName}`}
            className="text-sm font-regular text-partner"
          >
            @{transaction.model.ofUserName}
          </Link>
        </ResponsiveTooltip>
      </div>

      {/* Чип кампании с датой и процентом */}
      <div className="flex items-center gap-2">
        <Chip
          size="sm"
          color="secondary"
          variant="flat"
          className="!text-xs !text-accent"
          classNames={{
            content: '!text-xs !text-accent',
          }}
        >
          {chipLabel}
        </Chip>
        <ResponsiveTooltip content={t('transactions.campaign.byLinkTooltip')} placement="top">
          <div>
            <Typography variant="label3" className="md:font-semibold">
              {transaction.company?.link?.toLowerCase().includes('trial') ? 'Free-Trial' : t('transactions.campaign.byLink')}
            </Typography>
          </div>
        </ResponsiveTooltip>
      </div>
      <div className="flex items-center gap-1">
        <Snippet
          symbol={null}
          codeString={linkHref}
          className="bg-husky rounded-full px-2 py-[0.1875rem]"
          classNames={{
            base: 'bg-husky rounded-full px-2 py-[0.1875rem] flex items-center gap-1 shadow-none',
            pre: 'bg-transparent p-0 flex items-center gap-1 max-w-[13.75rem]',
            copyButton: 'text-partner min-w-5 h-5',
            checkIcon: 'text-partner',
            content: 'flex items-center gap-1 max-w-[13.75rem]',
          }}
        >
          <a href={linkHref} target="__blank" className="flex items-center gap-1 no-underline">
            <LinkIcon size={18} fill="#006FEE" className="text-partner" />
            <Typography variant="body2" className="!text-partner truncate max-w-[11.25rem]">
              {linkLabel}
            </Typography>
          </a>
        </Snippet>
        <div className="flex items-center gap-1">
          <ResponsiveTooltip content={t('transactions.campaign.guestsTooltip')} placement="top">
            <div className="flex items-center gap-1 cursor-pointer">
              <Icon icon="solar:eye-bold" width={16} height={16} className="text-default-400" />
              <span className="text-xs text-default-500">{clicksCount}</span>
            </div>
          </ResponsiveTooltip>
          <ResponsiveTooltip content={t('transactions.campaign.conversionTooltip')} placement="top">
            <span className="text-xs text-default-600 cursor-pointer">({subscribersPercent}%)</span>
          </ResponsiveTooltip>
        </div>
      </div>
    </div>
  );
};

export default TransactionCampaignCell;
